<template>
  <div class="modal-container">
    <Dialog :visible="display" :modal="true" class="unconditional-modal">
      <template #header>
        <h1 class="title pm-txt-color">{{ stepTitle }}</h1>
      </template>
      <div class="modal-content">
        <UnconditionalLoanSummaryStep
          v-if="currentStep === 'loanSummary'"
          :selectedLoanDetails="selectedLoanDetails"
          :formData="formData"
          :errors="loanSummaryErrors"
          :isErhl="isErhl"
          :interestRate="interestRate"
          :setupFeeLabel="setupFeeLabel"
          :setupFeePercentage="setupFeePercentage"
          :valuationCost="valuationCost"
          :loanDeposit="loanDeposit"
          :loanAmount="loanAmount"
          :equityReleaseTermYears="equityReleaseTermYears"
          :monthlyEquityReleaseAmount="monthlyEquityReleaseAmount"
          :lumpSumRequested="lumpSumRequested"
          :isPurchase="isPurchase"
          :mortgagePayoutEstimate="mortgagePayoutEstimate"
          :equityReleaseEndDate="equityReleaseEndDate"
          :formatErhlCurrency="formatErhlCurrency"
          :formatErhlYears="formatErhlYears"
          :formatErhlDate="formatErhlDate"
          :settlementDateLabel="settlementDateLabel"
          @update-form="onFormUpdate"
        />
        <UnconditionalNextOfKinStep
          v-if="currentStep === 'nextOfKin'"
          :value="nextOfKin"
          :errors="nextOfKinErrors"
          @update="onNextOfKinUpdate"
        />
        <UnconditionalBankDetailsStep
          v-if="currentStep === 'bankDetails'"
          :value="bankDetails"
          :errors="bankDetailsErrors"
          :serviceAgreementUrl="serviceAgreementUrl"
          @update="onBankDetailsUpdate"
        />
      </div>
      <template #footer>
        <div class="modal-footer">
          <!-- On the button row rather than below it, and rendered whether or not there is a
               message. Gated on `v-if` underneath the row, it claimed its own height the moment
               validation failed and walked the buttons down the screen mid-click. -->
          <p class="form-error error-text" role="alert" aria-live="polite">{{ formErrorMessage }}</p>
          <p class="close" @click="onBackClick">{{ currentStep === 'loanSummary' ? 'Back' : 'Previous' }}</p>
          <Button :disabled="isLoading" :label="primaryButtonLabel" type="primary" @click="onPrimaryAction" />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { API, TECHLEND_BE_URL } from '@/constants.api';
import {
  LUMP_SUM,
  PURCHASE_NEW_PROPERTY,
  SCHEDULED_EQUITY_RELEASE,
  findDrawdown,
  toMonthlyAmount,
} from '@/services/erhlSummary';
import { _patch } from '@/services/crudService';
import { currentDate } from '@/services/util';
import { fetchServiceAgreementUrl } from '@/services/serviceAgreement';
import { findApplicantContactMatches, mapAcceptOfferApiError } from '@/services/nextOfKinValidation';
import { formatCurrency, formatDate, formatErhlValue, monthsToYears, yearsFormatter } from '@/services/format';
import { getSetupFeeRate } from '@/services/setupFee';
import { mapActions, mapGetters } from 'vuex';
import { validateBankDetails } from '@/services/bankDetailsValidation';
import Dialog from 'primevue/dialog';
import Screensize from '@/mixins/screensize.mixin';
import UnconditionalBankDetailsStep from './UnconditionalBankDetailsStep.vue';
import UnconditionalLoanSummaryStep from './UnconditionalLoanSummaryStep.vue';
import UnconditionalNextOfKinStep from './UnconditionalNextOfKinStep.vue';

const CONTACT_RELATIONSHIPS = ['spouse_partner', 'child', 'sibling', 'parent', 'legal_representative', 'other'];

// Australian mobile: ten digits opening 04. Digits alone were accepted before, so a number far too
// short to dial passed validation.
const AU_MOBILE_PATTERN = /^04\d{8}$/;

// Sent in place of the solicitor details when none is involved. Every field has to be present and
// blank rather than absent: the endpoint validates with `whitelist: true`, so a payload carrying no
// solicitor keys is stripped to nothing and rejected.
const BLANK_SOLICITOR = Object.freeze({
  firstName: '',
  lastName: '',
  email: '',
  mobilePhoneNumber: '',
  company: '',
  line1: '',
  line2: '',
  city: '',
  country: '',
  postCode: '',
  state: '',
});

export default {
  mixins: [Screensize],
  props: ['display', 'openDialog', 'closeDialog'],
  components: { Dialog, UnconditionalLoanSummaryStep, UnconditionalNextOfKinStep, UnconditionalBankDetailsStep },
  data() {
    return {
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        mobilePhoneNumber: '',
        address: { line1: '', line2: '', city: '', postCode: '', state: '' },
        settlementDate: currentDate().toISOString(),
        company: '',
        // The tick box is optional, so it starts unchecked and a non-purchase can be submitted
        // without any solicitor details.
        solicitorInvolved: false,
      },
      currentStep: 'loanSummary',
      isLoading: false,
      formErrorMessage: '',
      loanSummaryErrors: {},
      nextOfKin: {
        firstName: '',
        lastName: '',
        email: '',
        mobilePhoneNumber: '',
        relationship: '',
        dateOfBirth: '',
        address: { line1: '', line2: '', city: '', postCode: '', state: '' },
        nextOfKinNotified: false,
        privacyPolicyProvided: false,
      },
      nextOfKinErrors: {},
      bankDetails: {
        bankAccountName: '',
        financialInstitutionName: '',
        bsb: '',
        accountNumber: '',
        directDebitAuthority: false,
        accountHolderAuthority: false,
      },
      bankDetailsErrors: {},
      // Resolved when the dialog opens, so the customer sees — and we record — the agreement version
      // in effect at that moment rather than one baked in at build time.
      serviceAgreementUrl: '',
    };
  },
  computed: {
    ...mapGetters({
      selectedLoanDetails: 'loans/selectedLoanDetails',
      selectedLoanId: 'loans/selectedLoanId',
      selectedTaskId: 'loans/selectedTaskId',
      isErhl: 'loans/isErhl',
    }),
    valuationCost() {
      return this.selectedLoanDetails.calculatedValue?.valuationFee;
    },
    setupFeeLabel() {
      return this.isErhl ? 'Set-up fee (%)' : 'Total set-up fee (%)';
    },
    setupFeePercentage() {
      return getSetupFeeRate(this.selectedLoanDetails.calculatedValue, this.isErhl);
    },
    interestRate() {
      const introRate = this.selectedLoanDetails.assessmentRates?.find(
        (rate) => rate.assessmentRateType === 'introductory_variable_interest_rate',
      );
      return introRate?.assessmentRate ?? this.selectedLoanDetails.assessmentRates?.[0]?.assessmentRate ?? 0;
    },
    loanDeposit() {
      return this.selectedLoanDetails.calculatedValue?.depositAmount;
    },
    loanAmount() {
      return this.selectedLoanDetails.calculatedValue?.loanAmount;
    },
    erhlDrawdowns() {
      return this.selectedLoanDetails?.erhlDetails?.drawdowns ?? [];
    },
    erhlScheduledEquityRelease() {
      return findDrawdown(this.erhlDrawdowns, SCHEDULED_EQUITY_RELEASE);
    },
    erhlLumpSum() {
      return findDrawdown(this.erhlDrawdowns, LUMP_SUM);
    },
    equityReleaseTermYears() {
      return monthsToYears(this.erhlScheduledEquityRelease?.durationTotalMonths);
    },
    monthlyEquityReleaseAmount() {
      return toMonthlyAmount(this.erhlScheduledEquityRelease);
    },
    lumpSumRequested() {
      return this.erhlLumpSum?.amount ?? 0;
    },
    equityReleaseEndDate() {
      return this.erhlScheduledEquityRelease?.effectiveTo ?? '';
    },
    isPurchaseNewProperty() {
      const loanPurpose = this.selectedLoanDetails?.loanPurpose;
      const erhlLoanPurposes = this.selectedLoanDetails?.erhlDetails?.loanPurposes ?? [];
      return loanPurpose === PURCHASE_NEW_PROPERTY || erhlLoanPurposes.includes(PURCHASE_NEW_PROPERTY);
    },
    hasIncomingProperty() {
      return (this.selectedLoanDetails?.incomingProperties ?? []).length > 0;
    },
    isPurchase() {
      return this.isPurchaseNewProperty || this.hasIncomingProperty;
    },
    // A purchase always conveys, so its details are shown and required outright. A non-purchase
    // only asks for them once the optional tick box is checked.
    showSolicitorDetails() {
      return this.isPurchase || Boolean(this.formData.solicitorInvolved);
    },
    mortgagePayoutEstimate() {
      return this.selectedLoanDetails?.calculatedValue?.mortgagePayoutEstimate ?? 0;
    },
    settlementDateLabel() {
      if (!this.isErhl) return 'Settlement Date';
      return 'Settlement date';
    },
    isNextOfKinRequired() {
      const applicants = this.selectedLoanDetails?.applicants ?? [];
      if (applicants.length !== 1) return false;
      // Computed from date of birth rather than the applicant's `age` field: that field is a
      // calendar-year subtraction with no birthday adjustment, so it reads a year high for anyone
      // yet to have their birthday and would show this step to some 64 year olds. The server
      // resolves the age the same way, so the two agree on who has to complete it.
      const age = this.calculateAge(applicants[0]?.dateOfBirth);
      return age !== null && age >= 65;
    },
    stepOrder() {
      if (!this.isErhl) return ['loanSummary'];
      return this.isNextOfKinRequired ? ['loanSummary', 'bankDetails', 'nextOfKin'] : ['loanSummary', 'bankDetails'];
    },
    // Each step names itself in the dialog header, the way the update bank details modal does, so
    // no step repeats its own title inside the body.
    stepTitle() {
      const titles = {
        loanSummary: 'Accept Unconditional Offer',
        bankDetails: 'Set up your direct debit',
        nextOfKin: 'Next of kin details',
      };
      return titles[this.currentStep] ?? 'Accept Unconditional Offer';
    },
    primaryButtonLabel() {
      if (this.currentStep === 'loanSummary' && !this.isErhl) return 'Submit';
      // On the ERHL flow the bank details step is only last when no next of kin step follows, so
      // this alone switches its label between 'Continue' and 'Submit' without a separate check.
      if (this.currentStep === this.stepOrder[this.stepOrder.length - 1]) return 'Submit';
      return 'Continue';
    },
  },
  watch: {
    // The dialog is mounted for the life of the page and toggled via `display`, so the fetch hangs
    // off the transition to open rather than a lifecycle hook. Re-fetching on each open also means a
    // newly published agreement is picked up without a reload.
    display: {
      immediate: true,
      async handler(isOpen) {
        if (!isOpen || !this.isErhl) return;
        this.serviceAgreementUrl = await fetchServiceAgreementUrl(this.selectedLoanId);
      },
    },
  },
  methods: {
    ...mapActions({ getSolictorDetailsById: 'loans/getSolictorDetailsById' }),
    formatErhlCurrency(value) {
      return formatErhlValue(value, (num) => formatCurrency(num));
    },
    formatErhlYears(value) {
      return formatErhlValue(value, yearsFormatter);
    },
    formatErhlDate(value) {
      return formatErhlValue(value, formatDate);
    },
    normalizeDate(value) {
      if (!value) return '';
      if (value instanceof Date) return value.toISOString().slice(0, 10);
      if (typeof value === 'string') return value.slice(0, 10);
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return '';
      return parsed.toISOString().slice(0, 10);
    },
    isEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '');
    },
    /** Age as at today, or null when the date of birth is missing or unparseable. */
    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return null;
      const dateOnly = typeof dateOfBirth === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOfBirth.trim()) : null;
      let date;
      if (dateOnly) {
        // Built from its parts so the value lands on local midnight: `new Date('1961-06-01')` is parsed as
        // UTC, which reads back as the day before wherever the offset is negative. The component
        // constructor rolls impossible dates forward (Feb 30 becomes Mar 2), so check the parts survived.
        const [year, month, day] = [Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3])];
        date = new Date(year, month - 1, day);
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
      } else {
        date = new Date(dateOfBirth);
      }
      if (Number.isNaN(date.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age -= 1;
      return age;
    },
    isAddressValid(address) {
      return Boolean(address?.line1 && address?.city && address?.postCode && address?.state);
    },
    onFormUpdate(nextForm) {
      this.loanSummaryErrors = {};
      this.formData = nextForm;
    },
    onNextOfKinUpdate(nextData) {
      this.nextOfKinErrors = {};
      this.nextOfKin = nextData;
    },
    onBankDetailsUpdate(nextData) {
      this.bankDetailsErrors = {};
      this.bankDetails = nextData;
    },
    onBackClick() {
      const currentIndex = this.stepOrder.indexOf(this.currentStep);
      if (currentIndex <= 0) {
        this.closeDialog();
        return;
      }
      this.formErrorMessage = '';
      this.currentStep = this.stepOrder[currentIndex - 1];
    },
    validateLoanSummary() {
      const errors = {};
      if (this.showSolicitorDetails) {
        if (!this.formData.firstName) errors.firstName = 'First name is required';
        if (!this.formData.lastName) errors.lastName = 'Last name is required';
        if (!this.formData.email) errors.email = 'Email is required';
        else if (!this.isEmail(this.formData.email)) errors.email = 'Please enter a valid email';
        if (!this.formData.company) errors.company = 'Company name is required';
        if (!this.formData.mobilePhoneNumber) errors.mobilePhoneNumber = 'Phone number is required';
        else if (!/^\d+$/.test(this.formData.mobilePhoneNumber))
          errors.mobilePhoneNumber = 'Phone number must be numeric';
        if (!this.isAddressValid(this.formData.address)) errors.address = 'Address is required';
      }
      if (!this.formData.settlementDate) errors.settlementDate = 'Settlement date is required';
      this.loanSummaryErrors = errors;
      return !Object.keys(errors).length;
    },
    validateNextOfKin() {
      const errors = {};
      if (!this.nextOfKin.firstName) errors.firstName = 'First name is required';
      if (!this.nextOfKin.lastName) errors.lastName = 'Last name is required';
      if (!this.nextOfKin.email) errors.email = 'Email is required';
      else if (!this.isEmail(this.nextOfKin.email)) errors.email = 'Please enter a valid email';
      if (!this.nextOfKin.mobilePhoneNumber) errors.mobilePhoneNumber = 'Phone number is required';
      else if (!AU_MOBILE_PATTERN.test(this.nextOfKin.mobilePhoneNumber))
        errors.mobilePhoneNumber = 'Enter a valid Australian mobile number, starting with 04';
      if (!this.nextOfKin.relationship) errors.relationship = 'Relationship is required';
      else if (!CONTACT_RELATIONSHIPS.includes(this.nextOfKin.relationship))
        errors.relationship = 'Please select a valid relationship';
      if (!this.nextOfKin.dateOfBirth) errors.dateOfBirth = 'Date of birth is required';
      else {
        // Each end of the permitted range says which end was missed, rather than restating the range.
        const nextOfKinAge = this.calculateAge(this.nextOfKin.dateOfBirth);
        if (nextOfKinAge === null) errors.dateOfBirth = 'Please enter a valid date of birth';
        else if (nextOfKinAge < 18) errors.dateOfBirth = 'Next of kin must be 18 or older';
        else if (nextOfKinAge > 80) errors.dateOfBirth = 'Next of kin cannot be older than 80';
      }
      if (!this.isAddressValid(this.nextOfKin.address)) errors.address = 'Address is required';
      // Caught here as well as by the API so the customer sees which field to change rather than a
      // rejected submission. The API is still the guard — this check only saves the round trip.
      Object.assign(errors, findApplicantContactMatches(this.nextOfKin, this.selectedLoanDetails?.applicants));
      if (!this.nextOfKin.nextOfKinNotified)
        errors.nextOfKinNotified = 'Please confirm you have notified your next of kin and have their consent';
      if (!this.nextOfKin.privacyPolicyProvided)
        errors.privacyPolicyProvided = 'Please confirm your next of kin has agreed and received the Privacy Policy';
      this.nextOfKinErrors = errors;
      return !Object.keys(errors).length;
    },
    validateBankDetails() {
      this.bankDetailsErrors = validateBankDetails(this.bankDetails);
      return !Object.keys(this.bankDetailsErrors).length;
    },
    buildPayload() {
      const { line1, line2, city, postCode, state } = this.formData.address;
      // The solicitor block is always sent, but blanked out when no solicitor is involved. Blank is
      // what the backend reads as "none involved": it skips creating one and leaves any solicitor
      // already on the application alone. The block cannot simply be omitted — the request is
      // validated with `whitelist: true`, which strips every key that is not a solicitor field, so
      // a payload without them arrives empty and is rejected as `request-body-required-error`.
      const solicitor = this.showSolicitorDetails
        ? {
            firstName: this.formData.firstName,
            lastName: this.formData.lastName,
            email: this.formData.email,
            mobilePhoneNumber: this.formData.mobilePhoneNumber,
            company: this.formData.company,
            line1: line1 || '',
            line2: line2 || '',
            city: city || '',
            country: 'au',
            postCode: postCode != null ? String(postCode) : '',
            state: state != null ? String(state).toLowerCase() : '',
          }
        : BLANK_SOLICITOR;
      const basePayload = {
        ...solicitor,
        applicationId: this.selectedLoanId,
        settlementDate: this.normalizeDate(this.formData.settlementDate),
        status: 'in_progress',
      };
      if (!this.isErhl) return basePayload;

      const erhlPayload = {
        directDebitAuthority: this.bankDetails.directDebitAuthority,
        accountHolderAuthority: this.bankDetails.accountHolderAuthority,
        bankAccount: {
          bankAccountName: this.bankDetails.bankAccountName,
          financialInstitutionName: this.bankDetails.financialInstitutionName,
          bsb: this.bankDetails.bsb,
          accountNumber: this.bankDetails.accountNumber,
        },
      };

      // Record the exact agreement version the customer was shown when they ticked the authority.
      // Omitted when the lookup gave us nothing, in which case the backend stamps the currently
      // effective version on the mandate itself rather than leaving the reference blank.
      if (this.serviceAgreementUrl) {
        erhlPayload.serviceAgreementUrl = this.serviceAgreementUrl;
      }

      if (this.isNextOfKinRequired) {
        const nextOfKinAddress = this.nextOfKin.address ?? {};
        erhlPayload.nextOfKin = {
          firstName: this.nextOfKin.firstName,
          lastName: this.nextOfKin.lastName,
          mobilePhoneNumber: this.nextOfKin.mobilePhoneNumber,
          email: this.nextOfKin.email,
          addressLine1: nextOfKinAddress.line1 != null ? String(nextOfKinAddress.line1) : '',
          addressLine2: nextOfKinAddress.line2 != null ? String(nextOfKinAddress.line2) : '',
          suburb: nextOfKinAddress.city != null ? String(nextOfKinAddress.city) : '',
          postCode: nextOfKinAddress.postCode != null ? String(nextOfKinAddress.postCode) : '',
          state: (nextOfKinAddress.state != null ? String(nextOfKinAddress.state) : '').toLowerCase(),
          dateOfBirth: this.normalizeDate(this.nextOfKin.dateOfBirth),
          relationship: this.nextOfKin.relationship,
          authorityToContact: true,
        };
      }

      return { ...basePayload, erhl: erhlPayload };
    },
    async save() {
      this.isLoading = true;
      try {
        await _patch(
          TECHLEND_BE_URL + API.TASKS.ACCEPT_UNCONDITIONAL_OFFER + `?taskId=${this.selectedTaskId}`,
          this.buildPayload(),
        );
        this.$emit('completed');
      } catch (err) {
        // The API replies with machine codes, and a failure inside the acceptance transaction
        // surfaces as `internal-server-error`; neither is any use to the customer, so everything is
        // mapped to copy and anchored to the step and field it belongs to.
        const { fieldErrors, message, step } = mapAcceptOfferApiError(err, {
          nextOfKin: this.nextOfKin,
          applicants: this.selectedLoanDetails?.applicants,
        });
        if (Object.keys(fieldErrors).length) {
          this.nextOfKinErrors = { ...this.nextOfKinErrors, ...fieldErrors };
        }
        if (step && this.stepOrder.includes(step)) this.currentStep = step;
        this.formErrorMessage = message;
      } finally {
        this.isLoading = false;
      }
    },
    async onPrimaryAction() {
      this.formErrorMessage = '';
      const currentIndex = this.stepOrder.indexOf(this.currentStep);
      const isFinalStep = currentIndex === this.stepOrder.length - 1;

      const isCurrentStepValid =
        this.currentStep === 'loanSummary'
          ? this.validateLoanSummary()
          : this.currentStep === 'nextOfKin'
          ? this.validateNextOfKin()
          : this.validateBankDetails();

      if (!isCurrentStepValid) {
        this.formErrorMessage = 'The form has missing or invalid fields. Please check before continuing.';
        return;
      }

      if (!isFinalStep) {
        this.currentStep = this.stepOrder[currentIndex + 1];
        return;
      }

      await this.save();
    },
    getSettlementDate(loan) {
      return loan?.settlementDate ?? loan?.incomingProperties?.[0]?.settlementDate ?? currentDate().toISOString();
    },
  },
  async created() {
    const data = await this.getSolictorDetailsById();
    const solicitor = data?.length ? data[0] : null;
    if (solicitor) {
      Object.assign(this.formData, {
        firstName: solicitor.firstName || '',
        lastName: solicitor.lastName || '',
        email: solicitor.email || '',
        mobilePhoneNumber: solicitor.mobilePhoneNumber || '',
        company: solicitor.company || '',
        address: {
          line1: solicitor.line1 || '',
          line2: solicitor.line2 || '',
          city: solicitor.city || '',
          postCode: solicitor.postCode || '',
          state: solicitor.state || '',
        },
        settlementDate: this.getSettlementDate(this.selectedLoanDetails),
      });
    } else {
      this.formData.settlementDate = this.getSettlementDate(this.selectedLoanDetails);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/global.scss';

.title {
  font-weight: bold;
  color: $primary-color;
  font-size: 2.25rem;
  @media (max-width: 992px) {
    font-size: 25px;
  }
}

// `modal-content` collides with the Bootstrap bundle that index.html pulls from the CDN, which
// paints a box around the content. That box is not in the design. The rule the same bundle draws
// above the buttons is, and is taken over deliberately below.
.modal-content {
  border: none;
}

.modal-footer {
  // The design carries a rule above the button row. It arrives here only as Bootstrap's
  // `.modal-footer` border, from the bundle index.html pulls off a CDN — so it is one class-name
  // collision away from vanishing, and the design work on this modal had already switched it off.
  // Declared explicitly instead, on the padding box so it spans the content on its own terms.
  border-top: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    border-top: 1px solid rgba(2, 0, 56, 0.15);
  }

  // Bootstrap's 12px is kept left/right — the design insets the buttons by that much — and above,
  // where it is the existing gap between the rule and the buttons. Its bottom 12px goes: that
  // stacked with the dialog's own bottom padding.
  padding: 12px 12px 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  // Without wrapping, a narrow viewport pushes the button past the dialog edge and the whole page
  // scrolls sideways. The gap also shrinks so the two still fit on one line where they can.
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-top: 2rem;

  .close {
    cursor: pointer;
  }

  // Bootstrap puts a 4px margin on every footer child, which fights the flex gap above.
  > * {
    margin: 0;
  }

  // Pushed to the edge opposite the buttons so the message reads level with the fields it refers
  // to instead of tucked underneath the row.
  > .form-error {
    margin-right: auto;
    // Back out the row's own inset so the message starts on the content's left edge rather than
    // level with the buttons, which are deliberately inset.
    margin-left: -12px;
    text-align: left;
    // Shorter than the button beside it, so filling the message in never changes the row height.
    min-height: 1.25rem;
    line-height: 1.25rem;
  }

  // The button ships with a right margin that would hold it clear of the dialog edge.
  :deep(.p-button) {
    margin-right: 0;
  }

  @media (max-width: 992px) {
    gap: 1rem;
  }
}

.error-text {
  color: red;
  font-size: 14px;
}

.modal-content {
  border: none;
}

:global(.unconditional-modal) {
  width: calc(100vw - 1rem);
  // Capped and laid out as header / scrolling body / footer. A centred dialog sized to its content
  // moves its own footer as that content grows, which is what shifted the buttons every time a
  // validation message appeared. Absorbing the growth in the body keeps the footer where it was.
  max-height: 90vh;
  display: flex;
  flex-direction: column;

  @media (min-width: 640px) {
    width: 80vw;
  }
}

:global(.unconditional-modal .p-dialog-header),
:global(.unconditional-modal .p-dialog-footer) {
  flex: 0 0 auto;
}

:global(.unconditional-modal .p-dialog-content) {
  flex: 1 1 auto;
  // A flex child otherwise refuses to shrink below its content height, so the cap above would grow
  // the dialog rather than scroll the body.
  min-height: 0;
  overflow-y: auto;
  // The design sets the button row 32px below the last field and 32px above the dialog edge. The
  // footer's own margin supplies the gap above, so the body padding must not add a second one.
  padding-bottom: 0;
}

:global(.unconditional-modal .p-dialog-footer) {
  padding-bottom: 2rem;
}

:global(.unconditional-modal .p-dialog-header),
:global(.unconditional-modal .p-dialog-footer) {
  flex: 0 0 auto;
}

:global(.unconditional-modal .p-dialog-content) {
  flex: 1 1 auto;
  // A flex child otherwise refuses to shrink below its content height, so the cap above would grow
  // the dialog rather than scroll the body.
  min-height: 0;
  overflow-y: auto;
}

// Each field renders its validation label only when it has an error, so every message that
// appeared added its own height and pushed the fields under it down. The band below each field is
// reserved up front and the message drops into it. The row gap goes to zero by the same amount, so
// the spacing between rows is what it was.
:global(.unconditional-modal .inputs) {
  row-gap: 0;
}

:global(.unconditional-modal .inputs > *) {
  position: relative;
  padding-bottom: 1.25rem;
}

// `.text-red` is the address block's own error label; the shared inputs use `.error-text`. Direct
// children only — the address block nests fields of its own that must keep their inline labels.
:global(.unconditional-modal .inputs > * > .error-text),
:global(.unconditional-modal .inputs > * > .text-red) {
  position: absolute;
  left: 0;
  bottom: 0;
  margin: 0;
  line-height: 1.25rem;
}

// Email/Phone in this row can show a two-sentence "already an applicant" error that wraps to two
// lines at this column width; give this row's band room for two lines instead of the shared one-line
// band, same idea as the .consent/.authority fix below.
:global(.unconditional-modal .inputs-personal > *) {
  padding-bottom: 2.5rem;
}

// Same reserve for the two acknowledgements and for the settlement date, whose labels sit outside
// the field grid. Every message in the panel has to be reserved, not just the ones with fields
// under them: the panel is only capped at 90vh, so below that height anything the content gains is
// gained by the dialog, and a centred dialog that gets taller moves both its own edges.
// `.consent` is the next of kin step's pair of attestations; it is named separately from the bank
// step's `.authority` blocks, so both have to be listed. Any new block that renders a validation
// label outside the field grid needs adding here, or its message will move the panel again.
:global(.unconditional-modal .authority),
:global(.unconditional-modal .consent),
:global(.unconditional-modal .section-3) {
  position: relative;
  padding-bottom: 1.25rem;
}

:global(.unconditional-modal .authority > .error-text),
:global(.unconditional-modal .consent > .error-text),
:global(.unconditional-modal .section-3 > .error-text) {
  position: absolute;
  left: 0;
  bottom: 0;
  margin: 0;
  line-height: 1.25rem;
}

// The label is anchored to the foot of its band, so a message too long for one line grows upward
// into the copy it belongs to rather than downward into empty space. The acknowledgements carry the
// longest messages and start wrapping around here, so their band holds two lines from this width
// down. Any message that can wrap needs a band deep enough for every line it can take.
@media (max-width: 480px) {
  :global(.unconditional-modal .authority),
  :global(.unconditional-modal .consent) {
    padding-bottom: 2.5rem;
  }
}
</style>
